import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Activity, Cable as CableIcon, AlertCircle } from 'lucide-react';
import { api } from '../../utils/api';
import {
  getChassisClass,
  isPortConnected,
  formatEquipmentTypeLabel,
  resolveLocalCableEndpoint,
} from '../../utils/deviceFaceplate.logic';
import { useDcStore } from '../../stores/dcStore';

interface Port {
  id: string;
  name: string;
  port_type: string;
  connector_type?: string;
  status?: string;
}

interface Cable {
  id: string;
  port_a_id: string;
  port_b_id: string;
  cable_type?: string;
  color?: string;
}

interface Equipment {
  id: string;
  name: string;
  equipment_type: string;
  u_height: number;
  mounting_position?: string;
  ports?: Port[];
  manufacturer?: string;
  model?: string;
}

interface TraceResult {
  cable_id?: string;
  cable_type?: string;
  dest_equipment_id?: string;
  dest_equipment_name?: string;
  dest_port_name?: string;
  dest_rack_id?: number | string;
  dest_rack_name?: string;
}

interface DeviceFaceplateViewerProps {
  device: Equipment;
  cables?: Cable[];
  allEquipment?: Equipment[];
  isLoading?: boolean;
  dcCode?: string;
}

export const DeviceFaceplateViewer: React.FC<DeviceFaceplateViewerProps> = ({
  device,
  cables = [],
  allEquipment = [],
  isLoading = false,
  dcCode,
}) => {
  const { activeDcCode } = useDcStore();
  const effectiveDcCode = dcCode || activeDcCode;

  const [selectedPortId, setSelectedPortId] = useState<string | null>(null);
  const [traceData, setTraceData] = useState<TraceResult | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const ports = device.ports || [];

  // Helper to check if port has a cable connected locally
  const checkPortConnected = (portId: string) => {
    return isPortConnected(portId, cables);
  };

  // Fetch full trace when a port is selected
  useEffect(() => {
    if (!selectedPortId) {
      setTraceData(null);
      return;
    }

    const fetchTrace = async () => {
      setTraceLoading(true);
      try {
        const res = await api.get(`/api/cables/trace/${selectedPortId}`);
        const root = res.data?.root;
        
        if (root && root.nodes && root.nodes.length > 1) {
          const destNode = root.nodes[root.nodes.length - 1];
          const firstCable = root.nodes[0]?.cable_id;
          const cableObj = cables.find(c => c.id === firstCable);

          setTraceData({
            cable_id: firstCable || cableObj?.id,
            cable_type: root.nodes[0]?.cable_type || cableObj?.cable_type || 'Cat6 Patch Cord',
            dest_equipment_id: destNode.equipment_id,
            dest_equipment_name: destNode.equipment_name,
            dest_port_name: destNode.port_name,
            dest_rack_id: destNode.rack_id,
            dest_rack_name: destNode.rack_name
          });
        } else {
          // Fallback to local cable matching if API trace is single node
          const localResolved = resolveLocalCableEndpoint(selectedPortId, cables, allEquipment);
          setTraceData(localResolved);
        }
      } catch (err) {
        console.warn('Trace API failed, falling back to local cables', err);
        const localResolved = resolveLocalCableEndpoint(selectedPortId, cables, allEquipment);
        setTraceData(localResolved);
      } finally {
        setTraceLoading(false);
      }
    };

    fetchTrace();
  }, [selectedPortId, cables, allEquipment]);

  const selectedPort = ports.find(p => p.id === selectedPortId);
  const chassisClass = getChassisClass(device.name, device.manufacturer);

  return (
    <div className="bg-slate-900 border border-slate-800/50 rounded-xl p-5 shadow-sm flex flex-col gap-4">
      {/* Visual Header */}
      <div className="flex justify-between items-center border-b border-slate-800/20 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-slate-900/60 border border-slate-800/50 rounded text-cyan-400">
            <Cpu className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold font-mono text-slate-100 uppercase tracking-wider">{device.name}</h3>
            <p className="text-[10px] font-mono text-slate-400">2D-ФАСАД ОБОРУДОВАНИЯ И МАТРИЦА ПОРТОВ</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 text-[9px] font-mono rounded bg-slate-900/60 border border-slate-800/50 text-slate-300">
            {formatEquipmentTypeLabel(device.equipment_type)}
          </span>
          <span className="px-2 py-0.5 text-[9px] font-mono rounded bg-cyan-950/20 border border-cyan-800/50 text-cyan-400">
            {device.u_height}U
          </span>
        </div>
      </div>

      {/* 2D Chassis Hardware Renderer */}
      <div className={`relative w-full border-2 ${chassisClass} rounded-lg p-3 shadow-sm flex items-center justify-between overflow-hidden select-none`}>
        
        {/* Left Ear Plate */}
        <div className="w-4 h-full bg-slate-900/60 border-r border-slate-800/50 flex flex-col items-center justify-between py-1.5 shrink-0">
          <div className="w-2 h-2 rounded-full border border-slate-700 bg-slate-950 flex items-center justify-center text-[7px] text-slate-500 font-bold">+</div>
          <div className="w-2 h-2 rounded-full border border-slate-700 bg-slate-950 flex items-center justify-center text-[7px] text-slate-500 font-bold">+</div>
        </div>

        {/* Center Panel (Ventilation + Ports Grid) */}
        <div className="flex-1 px-4 py-2 flex flex-col gap-3 relative">
          
          {/* Ventilation Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4px_4px] pointer-events-none" />

          {/* Device Label & Status LEDs */}
          <div className="flex justify-between items-center z-10">
            <span className="text-[10px] font-mono font-bold text-slate-300 tracking-wider">
              {device.name}
            </span>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_#34d399] animate-pulse" title="Питание системы ОК" />
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_6px_#06b6d4]" title="Сетевой линк активен" />
            </div>
          </div>

          {/* Port LED Matrix */}
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-2 z-10">
            {isLoading ? (
              <div
                data-testid="faceplate-ports-loading"
                className="col-span-12 py-3 px-4 bg-slate-950/50 rounded border border-slate-800/60 flex items-center justify-center gap-2 text-xs font-mono text-cyan-400 animate-pulse"
              >
                <Activity className="w-4 h-4 animate-spin text-cyan-400" />
                <span>Загрузка конфигурации портов и кабелей...</span>
              </div>
            ) : ports.length === 0 ? (
              <div className="col-span-12 py-3 px-4 bg-slate-950/40 rounded border border-slate-800/40 text-center">
                <p className="text-[11px] font-mono text-slate-400">Порты не сконфигурированы для этого устройства.</p>
              </div>
            ) : (
              ports.map((p) => {
                const connected = checkPortConnected(p.id);
                const isSelected = selectedPortId === p.id;

                let portColor = 'bg-slate-950/60 border-slate-800/80 text-slate-300 hover:border-slate-600';
                if (isSelected) {
                  portColor = 'bg-purple-950/40 border-purple-500 text-purple-300 shadow-[0_0_8px_rgba(168,85,247,0.4)] animate-pulse';
                } else if (connected) {
                  portColor = 'bg-cyan-950/20 border-cyan-800/60 text-cyan-400 shadow-[0_0_4px_rgba(6,182,212,0.2)] hover:border-cyan-500';
                }

                return (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedPortId(isSelected ? null : p.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedPortId(isSelected ? null : p.id);
                      }
                    }}
                    className={`h-11 rounded border flex flex-col items-center justify-between py-1 cursor-pointer transition-colors duration-200 select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400 ${portColor}`}
                    title={`${p.name} (${p.connector_type || 'RJ45'}) ${connected ? '- Подключен (Клик для трассировки)' : '- Свободен'}`}
                    aria-label={`Порт ${p.name}, тип ${p.connector_type || 'RJ45'}, статус: ${connected ? 'Подключен' : 'Свободен'}`}
                  >
                    <span className="text-[7px] font-mono font-bold truncate max-w-full px-0.5">{p.name}</span>
                    
                    {/* Socket shape with LED */}
                    <div className="w-3.5 h-2.5 bg-slate-900/60 border border-slate-800/80 rounded-sm flex items-center justify-center relative shadow-inner">
                      <span className={`w-1 h-1 rounded-full ${connected ? 'bg-cyan-400 shadow-[0_0_4px_#06b6d4]' : 'bg-slate-800'}`} />
                    </div>

                    <span className="text-[6px] font-mono text-slate-500 scale-90">{p.connector_type || 'RJ45'}</span>
                  </div>
                );
              })
            )}
          </div>

        </div>

        {/* Right Ear Plate */}
        <div className="w-4 h-full bg-slate-900/60 border-l border-slate-800/50 flex flex-col items-center justify-between py-1.5 shrink-0">
          <div className="w-2 h-2 rounded-full border border-slate-700 bg-slate-950 flex items-center justify-center text-[7px] text-slate-500 font-bold">+</div>
          <div className="w-2 h-2 rounded-full border border-slate-700 bg-slate-950 flex items-center justify-center text-[7px] text-slate-500 font-bold">+</div>
        </div>

      </div>

      {/* Link Trace Inspector Panel */}
      {selectedPort ? (
        <div className="bg-purple-950/10 border border-purple-900/30 rounded-xl p-4 flex flex-col gap-3 text-xs font-mono animate-in fade-in duration-200">
          <div className="flex justify-between items-center border-b border-purple-900/20 pb-2">
            <span className="text-purple-400 font-bold flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-purple-400" />
              ИНСПЕКТОР ТРАССИРОВКИ // ПОРТ {selectedPort.name}
            </span>
            <button
              onClick={() => setSelectedPortId(null)}
              className="text-[10px] text-slate-300 hover:text-slate-100 px-2 py-0.5 bg-slate-900/40 rounded border border-slate-800/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none transition-colors duration-200"
            >
              Закрыть
            </button>
          </div>

          {traceLoading ? (
            <div className="p-4 text-center text-slate-300 animate-pulse">
              Расчет сквозного маршрута коммутации...
            </div>
          ) : traceData ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center bg-slate-900/40 p-3 rounded-lg border border-slate-800/50">
              
              {/* Source Port */}
              <div className="space-y-1">
                <p className="text-[10px] text-slate-400 uppercase">Исходный Порт</p>
                <p className="font-bold text-cyan-400">{device.name}</p>
                <p className="text-[11px] text-slate-300">Порт: {selectedPort.name} ({selectedPort.connector_type || 'RJ45'})</p>
              </div>

              {/* Cable Info */}
              <div className="flex flex-col items-center justify-center gap-1 text-center py-2 md:py-0 border-y md:border-y-0 md:border-x border-slate-800/40 px-2">
                <CableIcon className="w-4 h-4 text-indigo-400" />
                <span className="px-2 py-0.5 rounded bg-indigo-950/20 border border-indigo-800/50 text-indigo-400 text-[10px]">
                  {traceData.cable_type || 'CAT6A Patch Cord'}
                </span>
                <span className="text-[9px] text-slate-400">
                  ID: {traceData.cable_id ? traceData.cable_id.slice(0, 8) + '...' : 'Direct Link'}
                </span>
              </div>

              {/* Destination Device */}
              <div className="space-y-1 md:text-right">
                <p className="text-[10px] text-slate-400 uppercase">Целевое Устройство</p>
                {traceData.dest_equipment_id ? (
                  <Link
                    to={effectiveDcCode ? `/${effectiveDcCode}/equipment/${traceData.dest_equipment_id}` : `/equipment/${traceData.dest_equipment_id}`}
                    className="font-bold text-emerald-400 hover:text-emerald-300 hover:underline flex items-center md:justify-end gap-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
                  >
                    {traceData.dest_equipment_name || 'Целевое устройство'}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ) : (
                  <p className="font-bold text-slate-100">{traceData.dest_equipment_name || 'Подключено'}</p>
                )}
                <p className="text-[11px] text-slate-300">
                  Порт: {traceData.dest_port_name || 'N/A'} {traceData.dest_rack_name ? `(${traceData.dest_rack_name})` : ''}
                </p>
              </div>

            </div>
          ) : (
            <div className="p-3 bg-slate-900/40 border border-slate-800/40 rounded-lg text-slate-300 text-[11px] flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
              <span>
                Порт <strong className="text-slate-100">{selectedPort.name}</strong> свободен (не подключен к кабелю).
              </span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-[11px] font-mono text-slate-400 text-center">
          Выберите любой порт на фасаде для просмотра детальной трассировки соединения.
        </p>
      )}
    </div>
  );
};
